import { RequestHandler } from 'express';
import { Counter, Gauge, Histogram, Registry, collectDefaultMetrics } from 'prom-client';
import { MetricsOptions } from "./types";

// Create a registry to register metrics
const registry = new Registry();

// Track created metrics to prevent duplication
const createdMetrics = new Set<string>();

// Create HTTP request duration histogram only once
let httpRequestDurationMicroseconds: Histogram<string>;

// Export registry for adding custom metrics
export const metrics = {
  registry,

  /**
   * Create a new counter metric
   */
  createCounter: (name: string, help: string, labelNames: string[] = []) => {
    if (createdMetrics.has(name)) {
      return registry.getSingleMetric(name) as Counter<string>;
    }

    const counter = new Counter({ name, help, labelNames, registers: [registry] });
    createdMetrics.add(name);
    return counter;
  },

  /**
   * Create a new gauge metric
   */
  createGauge: (name: string, help: string, labelNames: string[] = []) => {
    if (createdMetrics.has(name)) {
      return registry.getSingleMetric(name) as Gauge<string>;
    }

    const gauge = new Gauge({ name, help, labelNames, registers: [registry] });
    createdMetrics.add(name);
    return gauge;
  },

  /**
   * Create a new histogram metric
   */
  createHistogram: (name: string, help: string, labelNames: string[] = [], buckets?: number[]) => {
    if (createdMetrics.has(name)) {
      return registry.getSingleMetric(name) as Histogram<string>;
    }

    const histogram = new Histogram({ name, help, labelNames, buckets, registers: [registry] });
    createdMetrics.add(name);
    return histogram;
  }
};

/**
 * Create metrics middleware
 *
 * @param options Metrics options
 * @returns Express middleware and metrics endpoint handler
 */
export function metricsMiddleware(options: MetricsOptions = {}): {
  middleware: RequestHandler;
  endpoint: RequestHandler;
} {
  const prefix = options.prefix || '';
  const metricName = `${prefix}http_request_duration_seconds`;

  // Set default labels if provided
  if (options.defaultLabels) {
    registry.setDefaultLabels(options.defaultLabels);
  }

  // IMPORTANT: Only collect default metrics if explicitly requested with collectDefaults
  // This avoids conflict with the framework's default metrics collection
  if (options.collectDefaults === true) {
    // Only collect default metrics once
    if (!createdMetrics.has('default_metrics')) {
      collectDefaultMetrics({
        register: registry,
        prefix
      });
      createdMetrics.add('default_metrics');
    }
  }

  // Create HTTP request duration histogram only once
  if (!httpRequestDurationMicroseconds) {
    // Check if metric already exists in registry
    const existingMetric = registry.getSingleMetric(metricName);

    if (existingMetric) {
      httpRequestDurationMicroseconds = existingMetric as Histogram<string>;
    } else {
      httpRequestDurationMicroseconds = new Histogram({
        name: metricName,
        help: 'Duration of HTTP requests in seconds',
        labelNames: ['method', 'route', 'status'],
        buckets: [0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10],
        registers: [registry]
      });
      createdMetrics.add(metricName);
    }
  }

  // Create middleware to track request metrics
  const middleware: RequestHandler = (req, res, next) => {
    const start = process.hrtime();

    // Record end time and calculate duration on response finish
    res.on('finish', () => {
      const end = process.hrtime(start);
      const duration = end[0] + end[1] / 1e9;

      // Exclude metrics endpoint from metrics to avoid recursion
      if (req.path !== (options.endpoint || '/metrics')) {
        httpRequestDurationMicroseconds
          .labels(req.method, req.path, res.statusCode.toString())
          .observe(duration);
      }
    });

    next();
  };

  // Create endpoint to expose metrics
  const endpoint: RequestHandler = (req, res) => {
    res.set('Content-Type', registry.contentType);
    res.end(registry.metrics());
  };

  return { middleware, endpoint };
}
